/**
 * ReviewControls — toolbar shelf for review workflow.
 *
 * Sprint 14 (#14) — Track Changes toggle wired through real
 * `Command::ToggleTrackChanges`. The toggle's "active" state binds
 * to `state.isTrackingChanges()` (broadcast on every
 * `SELECTION_CHANGED`) — NOT a local Solid signal — so a tracked-
 * changes toggle issued by macro, undo, or another tab stays in
 * sync. New Comment keeps its Sprint 7 behaviour; Accept/Reject All
 * dispatch the engine's `ACCEPT_ALL_REVISIONS` / `REJECT_ALL_REVISIONS`
 * (issue #262 — one undo step each).
 *
 * The author input also feeds `Command::SetReviewIdentity`, so
 * tracked revisions carry the typed author instead of the engine's
 * "You" default.
 */
import {
    createEffect,
    createMemo,
    createSignal,
    onCleanup,
    Show,
    type Component,
} from 'solid-js';
import {
    createEditorCommands,
    createEditorState,
    protectionAdmits,
    useEngine,
} from '@nge/core';
import { focusEditorInput } from './focus';
import './ReviewControls.css';

export interface ReviewControlsProps {
    /** Default author for new comments. */
    defaultAuthor?: string;
}

export const ReviewControls: Component<ReviewControlsProps> = (props) => {
    const cmd = createEditorCommands();
    const state = createEditorState();
    const engine = useEngine();
    /* Sprint 14 (#14) — track state comes from the engine via
     * Event::SelectionChanged.is_tracking_changes, NOT a local
     * Solid signal. */
    const tracking = () => state.isTrackingChanges();
    const [error, setError] = createSignal<string | null>(null);
    const [commentDraftOpen, setCommentDraftOpen] = createSignal(false);
    const [commentText, setCommentText] = createSignal('');
    const [commentAuthor, setCommentAuthor] = createSignal(
        props.defaultAuthor ?? 'You',
    );

    /* Issue #34/#51 follow-on — `createMemo`, NOT a plain closure. The
     * identity-push effect below reads this; a raw
     * `state.selection() !== undefined` closure re-notifies on EVERY
     * selection update, and `setReviewIdentity`'s reply is itself a
     * SELECTION_CHANGED — a self-sustaining dispatch storm that
     * throttled every real command behind it (found while chasing the
     * insert-latency regression). A boolean memo only notifies its
     * readers when the VALUE flips (false → true, once per boot). */
    const ready = createMemo(() => state.selection() !== undefined);
    /* Phase 3 (#39) — `Command::ToggleTrackChanges`, `AcceptRevision`,
     * and `RejectRevision` are all engine-gated while a header/footer
     * story is active (track-changes toggles are on the story
     * blocklist). Disable rather than let the engine's silent
     * `Event::Error` be the only signal. */
    const inStory = () => state.editingStory() !== undefined;
    const gatedTitle = (label: string) =>
        inStory() ? 'Not available while editing a header or footer' : label;
    /* Issue #345 — the open document's enforced protection: Track
     * Changes is locked by every mode (`trackedChanges` forces it on),
     * accepting / rejecting is refused by every mode, comments only by
     * `readOnly` / `forms` (`protectionAdmits`, generated from
     * `bridge::meta`). Disabled with a reason, never a silent refusal. */
    const protectedTitle = (label: string, type: string, locked = false) => {
        const mode = state.protection();
        if (mode === undefined) return gatedTitle(label);
        return locked || !protectionAdmits(mode, type)
            ? 'Not available: this document is protected'
            : gatedTitle(label);
    };
    const trackLocked = () => state.protection() !== undefined;
    const commentLocked = () => !protectionAdmits(state.protection(), 'INSERT_COMMENT');
    const revisionsLocked = () => !protectionAdmits(state.protection(), 'ACCEPT_ALL_REVISIONS');

    /* Propagate the author input to the engine review identity so
     * tracked revisions stop being stamped with the "You" default.
     * Empty date keeps the engine's per-mutation date stamping.
     *
     * Issue #55 — gated on `ready()` (the engine has emitted its first
     * SELECTION_CHANGED). This effect used to dispatch unconditionally on
     * mount — ReviewControls sits in the always-mounted toolbar row, so it
     * fired before `Command::Init` resolved, rejecting with "engine not
     * initialized"; void-discarded, that surfaced as an unhandled promise
     * rejection on every single boot. `ready()` tracks `state.selection()`,
     * which only becomes defined post-INIT, so the effect naturally
     * re-fires once the engine comes up. The `.catch` is a backstop for a
     * later, genuinely failed identity push (e.g. mid-flight worker
     * respawn) — never let this dispatch go unobserved again. */
    createEffect(() => {
        if (!ready()) return;
        void cmd.setReviewIdentity(commentAuthor(), '').catch((e: unknown) => {
            console.error('setReviewIdentity failed', e);
        });
    });

    /* Sprint 14 (#14) — dispatch + let the engine's
     * SelectionChanged reply drive the visual active state.
     * `tracking()` reads `state.isTrackingChanges()` so the toggle
     * lights up only when the engine confirms recording is on. */
    const onToggleTracking = async () => {
        if (inStory()) return;
        const next = !tracking();
        try {
            if (next) {
                /* Guarantee the identity lands before recording starts —
                 * the worker queue serializes it ahead of the toggle. */
                await cmd.setReviewIdentity(commentAuthor(), '');
            }
            await cmd.toggleTrackChanges(next);
        } catch (e) {
            setError(String(e));
            setTimeout(() => setError(null), 6000);
        }
    };

    /* Surface engine ERROR events tied to review actions in the
     * inline banner (separate from the FileMenu's global toast). */
    createEffect(() => {
        const unsub = engine.subscribe((evt) => {
            if (
                evt.type === 'ERROR' &&
                /* Issue #345 — protection refusals are toasted by the
                   ErrorToast. */
                evt.kind !== 'Protected' &&
                (evt.message.includes('ToggleTrackChanges') ||
                    evt.message.includes('AcceptRevision') ||
                    evt.message.includes('RejectRevision') ||
                    evt.message.includes('AllRevisions') ||
                    evt.message.includes('Comment'))
            ) {
                setError(evt.message);
                setTimeout(() => setError(null), 6000);
            }
        });
        onCleanup(unsub);
    });

    /* Draft focus lifecycle (issue #35). Opening the draft moves focus
       into its textarea so typing lands in the comment, never the
       document; closing (cancel, Escape, or submit) hands focus back to
       the editor so typing resumes in the document. */
    let draftTextarea: HTMLTextAreaElement | undefined;
    createEffect(() => {
        if (commentDraftOpen()) draftTextarea?.focus();
    });
    const closeDraft = () => {
        setCommentDraftOpen(false);
        setCommentText('');
        focusEditorInput();
    };

    const submitComment = async () => {
        if (!ready() || commentText().trim() === '') return;
        await cmd.insertComment(commentText().trim(), commentAuthor());
        setCommentText('');
        setCommentDraftOpen(false);
        focusEditorInput();
    };

    /* Issue #262 — one engine command each: the engine walks the
     * document in order (paragraph-mark revisions merge paragraphs),
     * keeps comment anchors in step and pushes ONE undo step. (These
     * used to loop over per-row accepts: neither atomic nor able to
     * resolve a tracked paragraph merge.) */
    const acceptAll = async () => {
        if (inStory()) return;
        await cmd.acceptAllRevisions();
    };

    const rejectAll = async () => {
        if (inStory()) return;
        await cmd.rejectAllRevisions();
    };

    return (
        <div class="nge-review" role="group" aria-label="Review">
            <button
                class="nge-btn nge-review__btn nge-review__btn--track"
                type="button"
                aria-label="Track changes"
                aria-pressed={tracking()}
                data-active={tracking()}
                title={protectedTitle(
                    tracking() ? 'Track Changes — recording' : 'Track Changes — off',
                    'TOGGLE_TRACK_CHANGES',
                    trackLocked(),
                )}
                disabled={inStory() || trackLocked()}
                onClick={() => void onToggleTracking()}
            >
                <span aria-hidden="true">⌖</span>
                <span>Track</span>
            </button>

            <button
                class="nge-btn nge-review__btn"
                type="button"
                aria-label="New comment"
                title={protectedTitle('New comment on selected range', 'INSERT_COMMENT')}
                disabled={!ready() || inStory() || commentLocked()}
                onClick={() => setCommentDraftOpen((v) => !v)}
            >
                <span aria-hidden="true">💬</span>
                <span>New comment</span>
            </button>

            <button
                class="nge-btn nge-review__btn"
                type="button"
                aria-label="Accept all revisions"
                title={protectedTitle('Accept all tracked changes', 'ACCEPT_ALL_REVISIONS')}
                disabled={inStory() || revisionsLocked()}
                onClick={() => void acceptAll()}
            >
                <span aria-hidden="true">✓✓</span>
                <span>Accept all</span>
            </button>

            <button
                class="nge-btn nge-review__btn"
                type="button"
                aria-label="Reject all revisions"
                title={protectedTitle('Reject all tracked changes', 'REJECT_ALL_REVISIONS')}
                disabled={inStory() || revisionsLocked()}
                onClick={() => void rejectAll()}
            >
                <span aria-hidden="true">✗✗</span>
                <span>Reject all</span>
            </button>

            <Show when={commentDraftOpen()}>
                <div
                    class="nge-review__draft"
                    role="dialog"
                    aria-label="New comment"
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') closeDraft();
                    }}
                >
                    <input
                        class="nge-review__input"
                        type="text"
                        placeholder="Author"
                        value={commentAuthor()}
                        onInput={(e) => setCommentAuthor(e.currentTarget.value)}
                    />
                    <textarea
                        ref={draftTextarea}
                        class="nge-review__textarea"
                        placeholder="Comment text…"
                        value={commentText()}
                        rows={3}
                        onInput={(e) => setCommentText(e.currentTarget.value)}
                    />
                    <div class="nge-review__draft-actions">
                        <button
                            class="nge-btn"
                            type="button"
                            onClick={closeDraft}
                        >
                            Cancel
                        </button>
                        <button
                            class="nge-btn nge-btn--primary"
                            type="button"
                            disabled={commentText().trim() === ''}
                            onClick={() => void submitComment()}
                        >
                            Comment
                        </button>
                    </div>
                </div>
            </Show>

            <Show when={error()}>
                <div class="nge-review__error" role="alert">{error()}</div>
            </Show>
        </div>
    );
};
